/**
 * Safely extends the global `window.TPH` object with additional methods.
 *
 * `window.TPH` is defined as a chained accessor property (see useSessionFlag.ts
 * and mocks/browser.ts) so multiple modules can each contribute their own
 * methods without stomping on one another. That chaining relies on the
 * property staying `configurable: true`. If a stale definition (e.g. from a
 * cached bundle/service worker predating that fix) left `window.TPH`
 * non-configurable, `Object.defineProperty` throws `Cannot redefine
 * property: TPH` and can crash the app. This merges onto the existing value
 * instead of throwing in that case.
 */
export function extendWindowTph(getExtension: (base: Record<string, any>) => Record<string, any>) {
    if (typeof window === 'undefined') {
        return;
    }

    const existingDescriptor = Object.getOwnPropertyDescriptor(window, 'TPH');
    const baseGetter = existingDescriptor?.get;

    try {
        Object.defineProperty(window, 'TPH', {
            enumerable: true,
            configurable: true,
            get() { return getExtension(baseGetter?.() ?? {}) }
        });
    } catch (err) {
        console.warn('Unable to redefine window.TPH; merging onto the existing value instead.', err);
        const current = (window as any).TPH;
        if (current && typeof current === 'object') {
            Object.assign(current, getExtension(current));
        }
    }
}
